package top.wcpe.allincore.dsl.compiler.ast

import top.wcpe.allincore.dsl.compiler.source.SourcePosition
import top.wcpe.allincore.dsl.ir.model.UiNode

/**
 * RFC-RECOMPOSITION：元素树位置的条件渲染 AST 节点。
 *
 * 统一表达 if/else if/else 与 when 两种语法形态：
 * - **if 形态**：subject = null；每个 branch 的 condition 是任意 boolean 表达式，values 列表为空
 * - **when 形态**：subject 是 when 主体；每个 branch 的 condition 为 null，values 列表非空
 *   （编译期 lower 时按 `subject == values[i]` OR 链拼接）
 *
 * branch body 必须是单根 element（多根编译期报 E841）。
 *
 * 通过 [ElementAst.conditionalContent] 字段挂载到包装元素上：
 * - 包装元素的 kind 固定为 [KIND_CONDITIONAL]，children 为空，仅承载位置 / id
 * - 实际分支元素由 [ConditionalContentAst] 描述
 *
 * 这与 tooltipContent / toastContent 的"特殊语法承载字段"模式一致，避免破坏 ElementAst 的 data class 结构。
 */
data class ConditionalContentAst(
    /** when 形态的 subject 表达式；if 形态为 null。 */
    val subject: ExprAst?,

    /** branches 顺序敏感：求值时按顺序判定，命中第一个返回。 */
    val branches: List<Branch>,

    /** else 分支体；不存在 else 时为 null。 */
    val elseBranch: ElementAst?,

    override val position: SourcePosition,
) : AstNode {

    /**
     * 单个 branch 描述。
     *
     * - if 形态：condition 非 null，values 为空
     * - when 形态：values 非空，condition 为 null
     */
    data class Branch(
        /** if 形态的条件表达式；when 形态为 null。 */
        val condition: ExprAst?,

        /** when 形态的值列表（仅字面量）；if 形态为空。 */
        val values: List<ExprAst>,

        /** branch body：单根 element。 */
        val body: ElementAst,

        val position: SourcePosition,
    )

    companion object {
        /**
         * 包装元素的 kind 占位字符串。
         *
         * <p>直接引用 IR 层的 [UiNode.KIND_CONDITIONAL] 单一来源，避免 dsl-compiler 与
         * dsl-ir 各自维护字符串副本——如果两边不同步，runtime 分派会失败。</p>
         *
         * <p>编译期把 ConditionalContentAst 包装到一个 kind="$cond" 的 ElementAst 中作为子节点，
         * 让既有 ElementAst 树形结构无需改动。</p>
         */
        const val KIND_CONDITIONAL: String = UiNode.KIND_CONDITIONAL
    }
}
